import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Copy, Globe2, Link2, Plus, Trash2, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { initials } from '../../lib/format';
import {
  COLORES, ROLES, ROL_CORTO, UNIDADES, listaZonas, mensajeError, nombreZona, partirRecordatorio,
} from '../../lib/calendario';
import CampoPersonas from './CampoPersonas';

/**
 * Crear un calendario, o la configuración de uno mío: General · Compartir · Enlace .ics.
 * `pestana` inicial: 'general' | 'compartir' | 'ics'. `preguntas` = usePreguntas() de la página (confirmaciones).
 */
export default function DialogoCalendario({ abierto, calendario, pestanaInicial = 'general', onCerrar, onGuardado, preguntas }) {
  const nuevo = !calendario;
  const [pestana, setPestana] = useState(pestanaInicial);
  useEffect(() => { if (abierto) setPestana(pestanaInicial); }, [abierto, pestanaInicial]);

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="cal-dialogo sm:max-w-[600px] p-0 gap-0" data-testid="cal-dialogo-calendario">
        <DialogHeader className="px-6 pt-5 pb-3 text-left">
          <DialogTitle>{nuevo ? 'Crear calendario' : `Configuración de «${calendario.nombre}»`}</DialogTitle>
          {nuevo && <DialogDescription>Un calendario aparte para un equipo, un proyecto o una agenda que quieras compartir.</DialogDescription>}
        </DialogHeader>
        {!nuevo && (
          <div className="cal-pestanas px-6" role="tablist">
            {[['general', 'General'], ['compartir', 'Compartir'], ['ics', 'Enlace .ics']].map(([k, t]) => (
              <button key={k} type="button" role="tab" aria-selected={pestana === k} className={`cal-pestana ${pestana === k ? 'is-activa' : ''}`} onClick={() => setPestana(k)} data-testid={`cal-pestana-${k}`}>
                {t}
              </button>
            ))}
          </div>
        )}
        <div className="cal-dialogo-cuerpo od-scroll">
          {(nuevo || pestana === 'general') && (
            <General calendario={calendario} onGuardado={onGuardado} onCerrar={onCerrar} />
          )}
          {!nuevo && pestana === 'compartir' && <Compartir calendario={calendario} preguntas={preguntas} />}
          {!nuevo && pestana === 'ics' && <EnlaceIcs calendario={calendario} onGuardado={onGuardado} preguntas={preguntas} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function General({ calendario, onGuardado, onCerrar }) {
  const [f, setF] = useState(() => ({
    nombre: calendario?.nombre || '',
    descripcion: calendario?.descripcion || '',
    color: calendario?.color_original || calendario?.color || COLORES[1].valor,
    zona: calendario?.zona || 'America/Mexico_City',
    recordatorios: calendario ? [...(calendario.recordatorios || [])] : [10],
    invitar_miembros: calendario ? !!calendario.invitar_miembros : false,
  }));
  // Calendario compartido (solo al crear): con quién y con qué permiso. Al guardar se comparte con cada uno.
  const [compartido, setCompartido] = useState(false);
  const [miembros, setMiembros] = useState([]);
  const [rolMiembros, setRolMiembros] = useState('editor');
  const user = useStore((s) => s.user);
  const [guardando, setGuardando] = useState(false);
  const zonas = useMemo(() => listaZonas(), []);
  const set = (c) => setF((x) => ({ ...x, ...c }));

  const guardar = async (e) => {
    e.preventDefault();
    if (!f.nombre.trim()) { toast.error('Ponle un nombre al calendario'); return; }
    setGuardando(true);
    try {
      const cuerpo = { ...f, invitar_miembros: calendario ? f.invitar_miembros : (compartido && f.invitar_miembros) };
      const { data } = calendario
        ? await api.patch(`/calendar/calendars/${calendario.id}`, cuerpo)
        : await api.post('/calendar/calendars', cuerpo);
      let fallidos = 0;
      if (!calendario && compartido) {
        for (const m of miembros) {
          try {
            await api.post(`/calendar/calendars/${data.id}/shares`, {
              rol: rolMiembros, avisar: true, ...(m.grupo ? { group_id: m.id, email: m.correo } : { email: m.correo }),
            });
          } catch { fallidos += 1; }
        }
      }
      if (fallidos) toast.error(`No se pudo compartir con ${fallidos} ${fallidos === 1 ? 'persona' : 'personas'}: revísalo en Compartir`);
      toast.success(calendario ? 'Calendario guardado' : (compartido && miembros.length
        ? `Calendario compartido con ${miembros.length - fallidos} ${miembros.length - fallidos === 1 ? 'persona o grupo' : 'personas o grupos'}`
        : 'Calendario creado'));
      onGuardado(data);
      if (!calendario) onCerrar();
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo guardar el calendario'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form className="flex flex-col gap-4 px-6 pb-6 pt-3" onSubmit={guardar}>
      <label className="cal-etiqueta">Nombre
        <input className="cal-input" value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} autoFocus={!calendario} maxLength={100} data-testid="cal-calendario-nombre" />
      </label>
      <label className="cal-etiqueta">Descripción
        <textarea className="cal-input" rows={2} value={f.descripcion} onChange={(e) => set({ descripcion: e.target.value })} />
      </label>
      <div className="cal-etiqueta">Color
        <div className="cal-colores">
          {COLORES.map((c) => (
            <button type="button" key={c.valor} className={`cal-color ${f.color === c.valor ? 'is-sel' : ''}`} style={{ background: c.valor }} onClick={() => set({ color: c.valor })} title={c.nombre} aria-label={c.nombre} />
          ))}
        </div>
      </div>
      <label className="cal-etiqueta">Zona horaria
        <select className="cal-select" value={f.zona} onChange={(e) => set({ zona: e.target.value })}>
          {zonas.map((z) => <option key={z} value={z}>{nombreZona(z)}</option>)}
        </select>
        {calendario?.principal && <span className="cal-ayuda">Es la zona con la que ves todo el calendario.</span>}
      </label>
      <div className="cal-etiqueta">Notificaciones de los eventos nuevos
        <div className="flex flex-col gap-2">
          {f.recordatorios.map((m, i) => {
            const { valor, unidad } = partirRecordatorio(m);
            const poner = (v, u) => {
              const l = [...f.recordatorios];
              l[i] = Math.max(0, Math.min(40320, Math.round(Number(v) || 0) * UNIDADES[u]));
              set({ recordatorios: l });
            };
            return (
              <div key={i} className="flex items-center gap-2">
                <input type="number" min="0" className="cal-input w-[72px]" value={valor} onChange={(e) => poner(e.target.value, unidad)} aria-label="Cantidad" />
                <select className="cal-select" value={unidad} onChange={(e) => poner(valor, e.target.value)} aria-label="Unidad">
                  <option value="minutos">minutos</option><option value="horas">horas</option><option value="dias">días</option><option value="semanas">semanas</option>
                </select>
                <span className="text-[13px]" style={{ color: 'var(--text-2)' }}>antes</span>
                <button type="button" className="cal-icono-btn" onClick={() => set({ recordatorios: f.recordatorios.filter((_, j) => j !== i) })} aria-label="Quitar"><X className="h-4 w-4" /></button>
              </div>
            );
          })}
          {f.recordatorios.length < 5 && (
            <button type="button" className="cal-mini-enlace self-start" onClick={() => set({ recordatorios: [...f.recordatorios, 30] })}><Plus className="h-3.5 w-3.5" /> Añadir notificación</button>
          )}
        </div>
      </div>
      {!calendario && (
        <label className="cal-check" data-testid="cal-calendario-compartido">
          <input type="checkbox" checked={compartido} onChange={(e) => { setCompartido(e.target.checked); if (e.target.checked) set({ invitar_miembros: true }); }} />
          Calendario compartido con un equipo
        </label>
      )}
      {!calendario && compartido && (
        <div className="cal-etiqueta">Miembros
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <CampoPersonas
                onElegir={(p) => setMiembros((l) => (l.some((x) => x.correo === p.correo) ? l : [...l, p]))}
                excluir={[user?.email || '', ...miembros.map((m) => m.correo)]}
                placeholder="Añadir personas o grupos"
                testId="cal-calendario-miembro"
              />
            </div>
            <select className="cal-select" value={rolMiembros} onChange={(e) => setRolMiembros(e.target.value)} aria-label="Permiso de los miembros">
              {ROLES.map((r) => <option key={r.valor} value={r.valor}>{r.texto}</option>)}
            </select>
          </div>
          {miembros.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {miembros.map((m) => (
                <span key={m.correo} className="cal-chip">
                  {m.nombre || m.correo}
                  <button type="button" onClick={() => setMiembros((l) => l.filter((x) => x.correo !== m.correo))} aria-label={`Quitar a ${m.nombre || m.correo}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <span className="cal-ayuda">Con «Hacer cambios» todos pueden crear y mover eventos en este calendario.</span>
        </div>
      )}
      {(calendario ? !calendario.principal : compartido) && (
        <label className="cal-check" data-testid="cal-calendario-invitar-miembros">
          <input type="checkbox" checked={f.invitar_miembros} onChange={(e) => set({ invitar_miembros: e.target.checked })} />
          <span>Invitar a todos los miembros a cada evento nuevo
            <span className="cal-ayuda block">A cada uno le llega el aviso y el evento le sale en su propio calendario.</span>
          </span>
        </label>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="od-btn-secondary" onClick={onCerrar}>{calendario ? 'Cerrar' : 'Cancelar'}</button>
        <button type="submit" className="od-btn-primary" disabled={guardando} data-testid="cal-calendario-guardar">{guardando ? 'Guardando…' : calendario ? 'Guardar' : 'Crear calendario'}</button>
      </div>
    </form>
  );
}

function Compartir({ calendario, preguntas }) {
  const user = useStore((s) => s.user);
  const [filas, setFilas] = useState(null);
  const [rol, setRol] = useState('lector');
  const [ocupado, setOcupado] = useState(false);
  const dominio = (user?.email || '').split('@')[1] || '';

  const cargar = () => api.get(`/calendar/calendars/${calendario.id}/shares`)
    .then(({ data }) => setFilas(data.compartidos || []))
    .catch((e) => { setFilas([]); toast.error(mensajeError(e, 'No se pudo cargar con quién se comparte')); });
  useEffect(() => { cargar(); }, [calendario.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const compartir = async (destino, papel = rol) => {
    // Dar permiso de cambios a toda la organización o a un dominio entero se pregunta antes
    if ((destino.scope === 'tenant' || destino.scope === 'domain') && (papel === 'editor' || papel === 'admin')) {
      const ok = await preguntas.confirmar({
        titulo: destino.scope === 'tenant' ? '¿Dejar que toda la organización haga cambios?' : `¿Dejar que todo @${dominio} haga cambios?`,
        mensaje: 'Cualquiera podrá crear, mover y borrar eventos de este calendario.',
        boton: 'Compartir', peligro: false,
      });
      if (!ok) return;
    }
    setOcupado(true);
    try {
      await api.post(`/calendar/calendars/${calendario.id}/shares`, { rol: papel, avisar: true, ...destino });
      toast.success('Compartido');
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo compartir'));
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async (f) => {
    const ok = await preguntas.confirmar({
      titulo: '¿Dejar de compartir?',
      mensaje: `${quien(f)} dejará de ver este calendario.`,
      boton: 'Dejar de compartir',
    });
    if (!ok) return;
    try {
      await api.delete(`/calendar/calendars/${calendario.id}/shares/${f.id}`);
      setFilas((l) => l.filter((x) => x.id !== f.id));
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo quitar'));
    }
  };

  const destinoDe = (f) => (f.scope === 'tenant' ? { scope: 'tenant' }
    : f.scope === 'domain' ? { scope: 'domain', domain: f.domain }
      : f.grupo ? { group_id: f.grupo.id }
        : { user_id: f.persona?.id });

  return (
    <div className="flex flex-col gap-4 px-6 pb-6 pt-3" data-testid="cal-compartir">
      <div className="cal-etiqueta">Compartir con personas o grupos de tu organización
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <CampoPersonas
              onElegir={(p) => compartir(p.grupo ? { group_id: p.id, email: p.correo } : { email: p.correo })}
              excluir={[user?.email || '']}
              placeholder="Correo o nombre"
              testId="cal-compartir-persona"
            />
          </div>
          <select className="cal-select" value={rol} onChange={(e) => setRol(e.target.value)} aria-label="Permiso">
            {ROLES.map((r) => <option key={r.valor} value={r.valor}>{r.texto}</option>)}
          </select>
        </div>
        <span className="cal-ayuda">Para gente de fuera usa el enlace .ics (solo lectura).</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="od-btn-secondary" disabled={ocupado} onClick={() => compartir({ scope: 'tenant' })} data-testid="cal-compartir-organizacion">
          <Building2 className="h-4 w-4" /> Toda la organización
        </button>
        {dominio && (
          <button type="button" className="od-btn-secondary" disabled={ocupado} onClick={() => compartir({ scope: 'domain', domain: dominio })}>
            <Globe2 className="h-4 w-4" /> Todo @{dominio}
          </button>
        )}
      </div>
      {calendario.principal && (
        <p className="cal-ayuda">Como en Google, tu organización ya puede ver si estás libre u ocupado en este calendario (sin detalles).</p>
      )}

      <div className="cal-seccion-titulo">Con quién se comparte</div>
      {filas === null ? <p className="cal-ayuda">Cargando…</p> : filas.length === 0 ? (
        <p className="cal-ayuda">Todavía no lo compartes con nadie.</p>
      ) : (
        <ul className="cal-compartidos">
          {filas.map((f) => (
            <li key={f.id}>
              <span className="cal-avatar">{f.scope === 'tenant' ? <Building2 className="h-3.5 w-3.5" /> : f.scope === 'domain' ? <Globe2 className="h-3.5 w-3.5" /> : f.grupo ? <Users className="h-3.5 w-3.5" /> : initials(f.persona?.nombre, f.persona?.correo)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{quien(f)}</span>
                {f.persona?.correo && <span className="block cal-detalle-sub truncate">{f.persona.correo}</span>}
                {f.grupo?.correo && <span className="block cal-detalle-sub truncate">{f.grupo.correo}</span>}
              </span>
              <select className="cal-select" value={f.rol} onChange={(e) => compartir(destinoDe(f), e.target.value)} aria-label={`Permiso de ${quien(f)}`}>
                {ROLES.map((r) => <option key={r.valor} value={r.valor}>{ROL_CORTO[r.valor]}</option>)}
              </select>
              <button type="button" className="cal-icono-btn" onClick={() => quitar(f)} aria-label={`Dejar de compartir con ${quien(f)}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const quien = (f) => (f.scope === 'tenant' ? 'Toda la organización'
  : f.scope === 'domain' ? `Todo @${f.domain}`
    : f.grupo ? (f.grupo.nombre || f.grupo.correo || 'Grupo')
      : (f.persona?.nombre || f.persona?.correo || f.email || 'Persona'));

function EnlaceIcs({ calendario, onGuardado, preguntas }) {
  const [detalle, setDetalle] = useState(!!calendario.ics_detalle);
  const [ocupado, setOcupado] = useState(false);
  const url = calendario.ics_url;

  const cambiar = async (activo, conDetalle = detalle, pregunta, rotar = false) => {
    if (pregunta) {
      const ok = await preguntas.confirmar(pregunta);
      if (!ok) return;
    }
    setOcupado(true);
    try {
      const { data } = await api.post(`/calendar/calendars/${calendario.id}/ics`, { activo, detalle: conDetalle, rotar });
      onGuardado(data);
      setDetalle(!!data.ics_detalle);
      toast.success(activo ? 'Enlace listo' : 'Enlace desactivado');
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cambiar el enlace'));
    } finally {
      setOcupado(false);
    }
  };

  const copiar = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('Enlace copiado'); } catch { toast.error('No se pudo copiar'); }
  };

  return (
    <div className="flex flex-col gap-4 px-6 pb-6 pt-3" data-testid="cal-ics">
      <p className="cal-ayuda" style={{ fontSize: 13 }}>
        Con este enlace secreto puedes suscribirte al calendario desde el iPhone, Google Calendar u Outlook (solo lectura).
        Quien tenga el enlace verá los eventos: no lo publiques.
      </p>
      {url ? (
        <>
          <div className="flex gap-2">
            <input className="cal-input flex-1 font-mono text-[12px]" readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Enlace .ics" data-testid="cal-ics-url" />
            <button type="button" className="od-btn-secondary" onClick={copiar}><Copy className="h-4 w-4" /> Copiar</button>
          </div>
          <p className="cal-ayuda">{calendario.ics_detalle ? 'Muestra todos los detalles de los eventos (los privados salen como «Ocupado»).' : 'Solo muestra bloques «Ocupado», sin detalles.'}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="od-btn-secondary" disabled={ocupado} onClick={() => cambiar(true, !calendario.ics_detalle, {
              titulo: calendario.ics_detalle ? '¿Ocultar los detalles?' : '¿Mostrar todos los detalles?',
              mensaje: 'El enlace sigue siendo el mismo: quien ya está suscrito verá el cambio en la próxima actualización.',
              boton: 'Cambiar', peligro: false,
            })}
            >
              {calendario.ics_detalle ? 'Solo libre/ocupado' : 'Mostrar detalles'}
            </button>
            <button type="button" className="od-btn-secondary" disabled={ocupado} onClick={() => cambiar(true, calendario.ics_detalle, {
              titulo: '¿Generar otro enlace?', mensaje: 'El enlace actual dejará de funcionar para quien lo tenga.', boton: 'Generar otro',
            }, true)} data-testid="cal-ics-rotar"
            >
              <Link2 className="h-4 w-4" /> Generar otro enlace
            </button>
            <button type="button" className="od-btn-secondary" style={{ color: 'var(--danger)' }} disabled={ocupado} onClick={() => cambiar(false, false, {
              titulo: '¿Desactivar el enlace?', mensaje: 'Quien esté suscrito dejará de recibir los eventos.', boton: 'Desactivar',
            })} data-testid="cal-ics-apagar"
            >
              Desactivar
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="cal-check">
            <input type="checkbox" checked={detalle} onChange={(e) => setDetalle(e.target.checked)} />
            Mostrar todos los detalles (si no, solo «Ocupado»)
          </label>
          <button type="button" className="od-btn-primary self-start" disabled={ocupado} onClick={() => cambiar(true, detalle, {
            titulo: 'Crear un enlace secreto', mensaje: 'Cualquiera que tenga el enlace podrá ver este calendario, sin iniciar sesión.', boton: 'Crear enlace', peligro: false,
          })} data-testid="cal-ics-activar"
          >
            <Link2 className="h-4 w-4" /> Crear enlace de suscripción
          </button>
        </>
      )}
    </div>
  );
}
